import React, { useState, useEffect } from 'react';

/**
 * AssessmentQuiz — CoThink Compass
 * Displays questions one at a time; tracks progress, answers, completion time.
 * VISUAL RESTYLE ONLY: localStorage recovery (compass_progress), the DynamoDB
 * answer-normalization block, all handlers, and the onComplete payload are
 * unchanged.
 */
const AssessmentQuiz = ({ questions, resultId, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [startTime] = useState(Date.now());
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  // Save progress to localStorage for recovery
  useEffect(() => {
    const progressData = {
      resultId,
      currentQuestionIndex,
      answers,
      startTime,
      questions
    };
    localStorage.setItem('compass_progress', JSON.stringify(progressData));
  }, [currentQuestionIndex, answers, resultId, startTime, questions]);

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.question_id]: answer
    }));
  };

  // Navigate to next question
  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Submit assessment
  const handleSubmit = () => {
    const completionTimeSeconds = Math.floor((Date.now() - startTime) / 1000);

    // Clear saved progress
    localStorage.removeItem('compass_progress');

    // Pass results to parent
    onComplete({
      result_id: resultId,
      answers,
      completion_time_seconds: completionTimeSeconds
    });
  };

  // Handle exit attempt
  const handleExit = () => {
    if (Object.keys(answers).length > 0) {
      setShowExitConfirm(true);
    } else {
      window.history.back();
    }
  };

  const isAnswered = answers[currentQuestion.question_id] !== undefined;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const allQuestionsAnswered = Object.keys(answers).length === totalQuestions;

  return (
    <div className="min-h-screen bg-navy-50 dark:bg-navy-900 pt-10 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-interface font-medium text-navy-700 dark:text-paper-200">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
            <span className="text-interface text-navy-500 dark:text-paper-400">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full h-2 bg-paper-400 dark:bg-navy-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-paper-50 dark:bg-navy-800 rounded-2xl border border-paper-400 dark:border-navy-700 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-navy-900 dark:text-paper-100 mb-5 leading-snug">
            {currentQuestion.question_text}
          </h2>

          {/* Answer Options */}
          {/* normalizeOptions converts DynamoDB array ["A) ...","B) ..."] → {A:"...",B:"..."}
              so answer keys are always A/B/C/D, matching correct_answer values stored in DynamoDB */}
          {(() => {
            const normalizedOptions = Array.isArray(currentQuestion.options)
              ? Object.fromEntries(
                  ['A','B','C','D'].slice(0, currentQuestion.options.length).map(
                    (letter, i) => [letter, currentQuestion.options[i].replace(/^[A-D]\)\s*/, '')]
                  )
                )
              : currentQuestion.options;
            return (
              <div className="space-y-4">
                {Object.entries(normalizedOptions).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => handleAnswerSelect(key)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-colors duration-200 ${
                      answers[currentQuestion.question_id] === key
                        ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900'
                        : 'border-paper-400 dark:border-navy-600 hover:border-emerald-300 hover:bg-paper-100 dark:hover:bg-navy-700'
                    }`}
                  >
                    <div className="flex items-start">
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-4 ${
                        answers[currentQuestion.question_id] === key
                          ? 'bg-emerald-600 text-white'
                          : 'bg-paper-300 dark:bg-navy-700 text-navy-600 dark:text-paper-300'
                      }`}>
                        {key}
                      </span>
                      <span className="flex-1 text-navy-700 dark:text-paper-200">{value}</span>
                    </div>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center gap-4">
          <button
            onClick={handleExit}
            className="px-6 py-3 border border-paper-500 dark:border-navy-600 text-navy-700 dark:text-paper-200 rounded-lg hover:bg-paper-100 dark:hover:bg-navy-800 transition-colors duration-200 font-medium"
          >
            Exit
          </button>

          <div className="flex gap-4">
            {currentQuestionIndex > 0 && (
              <button
                onClick={handlePrevious}
                className="px-6 py-3 border border-emerald-600 text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900 transition-colors duration-200 font-medium"
              >
                Previous
              </button>
            )}

            {!isLastQuestion ? (
              <button
                onClick={handleNext}
                disabled={!isAnswered}
                className={`px-8 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  isAnswered
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-paper-400 dark:bg-navy-700 text-navy-400 dark:text-paper-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!allQuestionsAnswered}
                className={`px-8 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                  allQuestionsAnswered
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-paper-400 dark:bg-navy-700 text-navy-400 dark:text-paper-400 cursor-not-allowed'
                }`}
              >
                Submit Assessment
              </button>
            )}
          </div>
        </div>

        {/* Answer Counter */}
        <div className="mt-6 text-center text-interface text-navy-600 dark:text-paper-300">
          {Object.keys(answers).length} of {totalQuestions} questions answered
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-navy-950/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-paper-100 dark:bg-navy-800 border border-paper-400 dark:border-navy-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <h3 className="font-display text-2xl font-bold text-navy-900 dark:text-paper-100 mb-4">
              Exit Assessment?
            </h3>
            <p className="text-navy-600 dark:text-paper-300 mb-6">
              Your progress will be saved, but you'll need to restart from this point.
              Are you sure you want to exit?
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors duration-200"
              >
                Continue Assessment
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex-1 px-6 py-3 border border-paper-500 dark:border-navy-600 text-navy-700 dark:text-paper-200 rounded-lg hover:bg-paper-200 dark:hover:bg-navy-700 transition-colors duration-200 font-medium"
              >
                Exit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentQuiz;
